import {
  isReference,
  isResource,
  type ProfileResource,
  parseReference,
  resolveId,
} from '@medplum/core'
import type {
  ExtractResource,
  HumanName,
  Reference,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import {
  capitalize as _capitalize,
  compact,
  identity,
  invert,
  pickBy,
  sampleSize,
  snakeCase,
  uniq,
} from 'lodash-es'
import { v4 as uuidV4 } from 'uuid'
import { invariant } from '#src/invariant.ts'
import type { JsonObject } from '#src/json.ts'

export function now(): string {
  return new Date().toISOString()
}

/**
 * Builds a resource from a resource type and a resource object.
 * @param resourceType - The resource type.
 * @param resource - The resource object.
 * @returns The built resource.
 * @deprecated Use ExtendedResource#build instead
 */
export function build<T extends ResourceType>(
  resourceType: T,
  resource: Omit<ExtractResource<T>, 'resourceType'>
): ExtractResource<T> {
  return {
    resourceType,
    ...resource,
  } as ExtractResource<T>
}

export function canonical(
  resource:
    | { url?: string | undefined; version?: string | undefined }
    | null
    | undefined
): string | undefined {
  if (!resource?.url) {
    return undefined
  }

  return resource.version ? `${resource.url}|${resource.version}` : resource.url
}

export function parseCanonical(
  canonical: string | undefined
): { url: string; version?: string } | undefined {
  if (!canonical) {
    return undefined
  }

  const parts = canonical.split('|')
  if (parts.length === 1) {
    return {
      url: parts[0],
    }
  }

  return {
    url: parts[0],
    version: parts[1],
  }
}

export type FhirValue<T extends ResourceType = ResourceType> =
  | ExtractResource<T>
  | Reference<ExtractResource<T>>

export function getType<T extends ResourceType>(value: FhirValue<T>): T
export function getType<T extends ResourceType>(
  value: FhirValue<T> | undefined
): T | undefined
export function getType<T extends ResourceType>(
  value: FhirValue<T> | undefined
): T | undefined {
  if (isResource(value)) {
    return value.resourceType
  }

  const referenceType = value?.type ?? parseReferenceSafe(value)[0]
  if (referenceType) {
    return referenceType as T
  }

  return undefined
}

export function isOfType<T extends ResourceType>(
  value: FhirValue | undefined,
  type: T
): value is FhirValue<T> {
  return getType(value) === type
}

export function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

export function isRecord(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function extractId(
  value: string | Resource | Reference | undefined | null
): string | undefined {
  if (isReference(value)) {
    return resolveId(value)
  }
  if (isResource(value)) {
    return value.id
  }
  if (typeof value === 'string') {
    const reference = parseReferenceString(value)
    if (reference?.id) {
      return reference.id
    }
    return value
  }
  return undefined
}

export function parseReferenceSafe<T extends Resource>(
  reference: Reference<T> | undefined
): [T['resourceType'], string] | [undefined, undefined] {
  try {
    return parseReference(reference)
  } catch (_e) {
    return [undefined, undefined]
  }
}

export function parseReferenceString<T extends Resource = Resource>(
  referenceString: string | undefined | null,
  display?: string | null
): Reference<T> | undefined {
  if (!referenceString) {
    return undefined
  }

  const [type, id] = referenceString.split('/')
  if (!type || !id) {
    return undefined
  }

  return {
    type: type as ResourceType,
    id,
    reference: referenceString,
    display: display ?? undefined,
  }
}

// The bare Composition id from a `resourceRef` like `Composition/{id}`, or
// undefined when the ref is absent, malformed, or not a Composition. Note-domain
// paths (chat note-scoping, "extract notes" CTAs, the note pill) use this to tell
// whether the edited resource is a note; non-Composition resources (e.g. a
// CarePlan) yield undefined.
export function noteIdFromResourceRef(
  resourceRef: string | undefined
): string | undefined {
  const parsed = parseReferenceString(resourceRef)
  return parsed?.type === 'Composition' ? parsed.id : undefined
}

export function officialNameOrFallback(
  profileResource: ProfileResource | undefined
): HumanName | undefined {
  return getHumanName(profileResource, {
    use: ['official', 'usual'],
    fallback: true,
  })
}

export function getHumanName<T extends { name?: HumanName[] | undefined }>(
  nameable: T | undefined,
  opts: { use?: HumanName['use'] | HumanName['use'][]; fallback?: boolean } = {}
): HumanName | undefined {
  let name: HumanName | undefined
  if (opts.use) {
    if (Array.isArray(opts.use)) {
      const uses = uniq(opts.use)
      for (const use of uses) {
        name = nameable?.name?.find((n) => n.use === use)
        if (name) {
          return name
        }
      }
    } else {
      name = nameable?.name?.find((n) => n.use === opts.use)
    }
  } else {
    name = nameable?.name?.[0]
  }

  if (name) {
    return name
  }

  // TODO: maybe a more intelligent fallback that ranks based on amount of available information
  // i.e. if for some reason the first entry is missing `family` and/or `given` but the second contains both,
  // we should likely prioritize the second entry
  return opts.fallback ? nameable?.name?.[0] : undefined
}

export function setHumanName(
  existingNames: HumanName[] | undefined,
  newName: HumanName
): HumanName[] {
  // Replace in place so positional fallbacks (e.g. name[0]) survive edits.
  const names = existingNames ?? []
  const index = names.findIndex((n) => n.use === newName.use)
  if (index === -1) {
    return [...names, newName]
  }
  return [
    ...names.slice(0, index),
    newName,
    ...names.slice(index + 1).filter((n) => n.use !== newName.use),
  ]
}

export function getNickname(
  profileResource: ProfileResource | undefined
): string | undefined {
  const name = getHumanName(profileResource, {
    use: ['nickname'],
  })
  return name ? humanNameToString(name) : undefined
}

export function getAllNicknames(
  profileResource: ProfileResource | undefined
): string[] {
  if (!profileResource?.name) {
    return []
  }
  return profileResource.name
    .filter((name) => name.use === 'nickname')
    .map((name) => name.given?.[0] ?? '')
    .filter(Boolean)
}

export interface HumanNameFormatOptions {
  all?: boolean
  prefix?: boolean
  suffix?: boolean
  use?: string
  nicknames?: string[]
}

/**
 * Formats a FHIR HumanName as a string. This implementation is based on @medplum/core's formatHumanName
 * with the addition of support for nicknames in parentheses.
 * @param name - The name to format.
 * @param options - Optional name format options, including our custom nicknames option.
 * @returns The formatted name string.
 */
export function formatHumanName(
  name: HumanName | undefined,
  options?: HumanNameFormatOptions
): string {
  if (!name) {
    return ''
  }

  const builder: string[] = []

  if (name.prefix && options?.prefix !== false) {
    builder.push(...name.prefix)
  }

  if (name.given) {
    builder.push(...name.given)
  }

  if (options?.nicknames?.length) {
    builder.push(`(${options.nicknames.join(' ')})`)
  }

  if (name.family) {
    builder.push(name.family)
  }

  if (name.suffix && options?.suffix !== false) {
    builder.push(...name.suffix)
  }

  if (name.use && (options?.all || options?.use)) {
    builder.push(`[${name.use}]`)
  }

  if (builder.length === 0) {
    const textStr = name.text
    if (textStr) {
      return textStr
    }
  }

  return builder.join(' ').trim()
}

export function containsCJKCharacters(name: string): boolean {
  // https://symbl.cc/en/unicode/blocks/cjk-unified-ideographs/
  // https://symbl.cc/en/unicode/blocks/hiragana/
  // https://symbl.cc/en/unicode/blocks/katakana/
  // https://symbl.cc/en/unicode/blocks/hangul-syllables/
  return /[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]/.test(name)
}

export function humanNameToString(name: HumanName): string {
  const family = name.family ?? ''
  const given = name.given?.join(' ') ?? ''
  return compact([family, given]).join(
    containsCJKCharacters(given) && containsCJKCharacters(family) ? '' : ', '
  )
}

// "Smith, John R." — middle given names abbreviate to initials. CJK names
// don't abbreviate (an initial of an ideograph is meaningless), so they fall
// back to the plain form.
export function humanNameToLastFirstMiddleInitialString(
  name: HumanName
): string {
  const family = name.family ?? ''
  const [first, ...middles] = name.given ?? []
  if (
    containsCJKCharacters(name.given?.join('') ?? '') ||
    containsCJKCharacters(family)
  ) {
    return humanNameToString(name)
  }
  const given = compact([
    first,
    ...middles.filter(Boolean).map((middle) => `${middle[0].toUpperCase()}.`),
  ]).join(' ')
  return compact([family, given]).join(', ')
}

export function cleanHumanName(name: HumanName): HumanName {
  return {
    ...name,
    given: name.given?.map((g) => g.trim()).filter(Boolean),
    family: name.family?.trim(),
    prefix: name.prefix?.map((p) => p.trim()).filter(Boolean),
    suffix: name.suffix?.map((s) => s.trim()).filter(Boolean),
  }
}

export function cleanHumanNames(
  names: HumanName[] | undefined
): HumanName[] | undefined {
  return names?.map(cleanHumanName)
}

export function extractVersion(
  reference: Reference | undefined
): string | undefined {
  if (!reference?.reference) {
    return undefined
  }

  const parts = reference.reference.split('/')
  const historyIndex = parts.indexOf('_history')
  if (historyIndex !== -1 && historyIndex + 1 < parts.length) {
    return parts[historyIndex + 1]
  }
  return undefined
}

export function hasReference(
  source: Reference | undefined,
  target: Reference | undefined
): boolean {
  if (!source || !target || !source.reference || !target.reference) {
    return false
  }
  return source.reference === target.reference
}

export function emptyFunction(..._a: any[]): void {}

/**
 * Generates a Uniform Resource Name (URN) using a UUID (Universally Unique Identifier).
 *
 * This function creates a URN by combining the "urn:uuid:" prefix with a newly generated UUID.
 * The UUID is generated using the v4() function, which creates a random UUID.
 *
 * https://datatracker.ietf.org/doc/html/rfc4122
 *
 * @returns A string in the format "urn:uuid:[UUID]", where [UUID] is a newly generated UUID.
 */
export function generateUrnUuid(): string {
  return `urn:uuid:${uuidV4()}`
}

export function generateUUID(): string {
  return uuidV4()
}

export function newTestGenerateUrnUuid(): () => string {
  let counter = 0
  return () => {
    counter++
    return `urn:uuid:00000000-0000-0000-0000-${counter.toString().padStart(12, '0')}`
  }
}

export function nullthrows<T>(
  value: T | null | undefined,
  message?: string
): T {
  invariant(value != null, message ?? 'Value must not be null or undefined')
  return value
}

export function isEnumValue<T extends Record<string, string>>(
  value: string | undefined | null,
  enumType: T
): value is T[keyof T] {
  return coerceToEnum(value, enumType) != null
}

export function coerceToEnum<T extends Record<string, string>>(
  value: string | undefined | null,
  enumType: T
): T[keyof T] | null {
  if (Object.values(enumType).includes(value as any)) {
    return value as T[keyof T]
  }
  return null
}

export function assertAsEnum<T extends Record<string, string>>(
  value: string | undefined | null,
  enumType: T
): T[keyof T] {
  const coerced = coerceToEnum(value, enumType)
  if (coerced == null) {
    throw new Error(`Invalid enum value: ${value}`)
  }
  return coerced
}

export function generateSeenMRN(): string {
  return (
    sampleSize('12356789', 1).join('') + sampleSize('012356789', 7).join('')
  )
}

export function identifier(system: string, value: string): string {
  return `${system}|${value}`
}

export const recordKeys = Object.keys as <
  T extends Record<PropertyKey, unknown>,
>(
  record: T
) => (keyof T)[]

export const recordValues = Object.values as <
  T extends Record<PropertyKey, unknown>,
>(
  record: T
) => Required<T>[keyof T][]

export const recordEntries = Object.entries as <
  T extends Record<PropertyKey, unknown>,
>(
  record: T
) => [keyof T, Required<T>[keyof T]][]

export const recordInvert = invert as <
  T extends Record<string, string | number | symbol>,
>(
  record: T
) => Record<T[keyof T], keyof T>

export type AnyFunction = (...args: any[]) => any

export function formatReference(reference?: Reference): string {
  return reference?.display ?? reference?.reference ?? ''
}

export async function sha256(buffer: string): Promise<string> {
  const bytes = new TextEncoder().encode(buffer)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return buf2Base64(digest)
}

// Based on Auth0's Call Your API Using the Authorization Code Flow with PKCE
// https://auth0.com/docs/get-started/authentication-and-authorization-flow/authorization-code-flow-with-pkce/call-your-api-using-the-authorization-code-flow-with-pkce#javascript-sample
export function buf2Base64(buffer: Uint8Array | ArrayBuffer): string {
  // match https://github.com/medplum/medplum/blob/885696b26de83cc3a94684b0eac71c3a8f1d9eeb/packages/server/src/oauth/token.ts#L18
  return btoa(
    String.fromCharCode.apply(null, Array.from(new Uint8Array(buffer)))
  )
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '')
}

/**
 * Removes undefined values from an object.
 * @param obj - The object to compact.
 * @returns The compacted object.
 */
export function compactObject<T extends Record<string, unknown>>(
  obj: T
): Partial<T> {
  return pickBy<T>(obj, identity)
}

export const mergeSystemValue = <V extends { system?: string }>(
  existing: V[] | null | undefined,
  newValue: V
): V[] => {
  if (!existing) {
    return [newValue]
  }

  return [...existing.filter((v) => v.system !== newValue.system), newValue]
}

export const mergeSystemValues = <V extends { system?: string }>(
  existing: V[] | null | undefined,
  ...newValues: V[]
): V[] =>
  newValues.reduce(
    (acc, newValue) => mergeSystemValue(acc, newValue),
    existing ?? []
  )

export function toCSV<T extends Record<string, unknown>>(
  data: T[],
  { columns }: { columns?: Record<keyof T, string> } = {}
): string {
  const result: string[] = []
  const columnOrder: (keyof T)[] = columns
    ? Object.keys(columns)
    : Object.keys(data[0] || {})

  if (columns) {
    result.push(columnOrder.map((key) => columns[key]).join(','))
  }

  const rows = data.map((row) =>
    columnOrder
      .map((key) => {
        const value = String(row[key] || '')
        return `"${value.replace(/"/g, '""')}"`
      })
      .join(',')
  )

  result.push(...rows)
  return result.join('\r\n')
}

/**
 * Converts a string to CONSTANT_CASE.
 * Note: contiguous numbers and letters are separated by an underscore: i.e. 180d -> 180_D
 * @param value - The string to convert.
 * @returns The CONSTANT_CASE string.
 */
export function constantCase(value: string): string {
  return snakeCase(value).toUpperCase()
}

export function capitalize<T extends string>(value: T): Capitalize<T> {
  return _capitalize(value) as Capitalize<T>
}

/**
 * Wraps a promise in a try/catch and returns null if an error occurs.
 * This function will never throw an error.
 * @param promise - The promise to wrap.
 * @returns The result of the promise or null if an error occurs.
 */
export async function safePromise<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise
  } catch (e) {
    console.error(e)
    return null
  }
}

/**
 * Parses the pre-signed storage url to extract the binary resource id
 * @param url - Pre-signed storage url
 * @returns id of `Binary` resource stored in Medplum
 */
export function getBinaryId(url?: string): string | undefined {
  if (!url) {
    return undefined
  }
  const urlObject = new URL(url)
  const parts = urlObject.pathname.split('/')
  return parts.at(-2)
}

const BINARY_ID_FROM_URL =
  /(?:^Binary\/|\/binary\/)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

// Unlike `getBinaryId`, also reads a relative `Binary/<id>` reference.
export function binaryIdFromAttachmentUrl(
  url: string | undefined
): string | undefined {
  if (!url) {
    return undefined
  }
  return url.match(BINARY_ID_FROM_URL)?.[1]
}

export function saveBlobWithName(blob: Blob, filename?: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename ?? 'file'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(objectUrl)
}

/**
 * Extracts filename from Content-Disposition header.
 * Supports both `filename="name.pdf"` and `filename=name.pdf` formats.
 */
export function getFilenameFromContentDisposition(
  header: string | undefined
): string | undefined {
  if (!header) {
    return undefined
  }
  // Handle quoted filename: filename="report.pdf"
  const quotedMatch = header.match(/filename="([^"]+)"/)
  if (quotedMatch?.[1]) {
    return quotedMatch[1]
  }
  // Handle unquoted filename: filename=report.pdf
  const unquotedMatch = header.match(/filename=([^;\s]+)/)
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1]
  }
  return undefined
}

/**
 * Extracts the resource type from a Medplum FHIR URL.
 * @param url - Medplum FHIR URL (e.g., "https://api.medplum.com/fhir/R4/Patient/123")
 * @returns The resource type or undefined if not found
 */
export function getTypeFromMedplumURL(url: string): ResourceType | undefined {
  const reference = url.split('/fhir/R4/').pop()
  return getType({ reference })
}
